<template>
  <div class="domain-info">
    <div class="domain-info__entry open">
      <div class="domain-info__content" style="display: block">
        <section class="covid-19">
          <div id="content__wrapper">
            <h1 class="mx-auto content__header">75%</h1>
            <section class="part part__one">
              <p>
                <span>
                  of businesses impacted by a major disaster fail within three
                  years.
                </span>
                We are currently in the midst of one of the largest disruptions
                to our economy in US history. Economic resilience is about
                rebounding in the face of business interruption.
              </p>
              <p>
                Many small and mid-sized businesses end up wasting a lot of
                time, effort and money on actions they think will be helpful in
                overcoming these disruptions.
              </p>
            </section>
            <section class="part part__two">
              <div class="blue__bar"></div>
              <h2>
                Wouldn’t it be helpful to know what other businesses like yours
                did in past disasters that worked, and what didn’t?
              </h2>
              <h2>
                Wouldn’t it be helpful to use this information to build a more
                cost-effective strategy to cope with the disruption?
              </h2>
              <div class="blue__bar"></div>
            </section>
            <img src="/images/Yeswereopen.jpg" alt class="content__image" />
            <section class="part part__three">
              <h2>The Business Resilience Calculator (BRC)</h2>
              <p>
                The Business Resilience Calculator (BRC) is decision-support
                software designed to help you overcome the various disruptions
                to your business when a disaster strikes and to do so in a
                cost-effective manner. You have a range of options to get your
                business back on its feet, and the BRC helps you identify them,
                measure their effectiveness and cost, and combine them into a
                least-cost—and hence profit-maximizing—strategy.
              </p>
              <h2>Covid-19 BRC Pre-Release</h2>
              <p>
                The BRC is scheduled to be released next year. A product of
                years of government-funded research at major universities in the
                US, the BRC is now being released in limited-scope to help small
                and mid-sized businesses disrupted by the current crisis. It
                includes a module tailored to the types of economic disruptions
                that can occur in pandemics. These include employees not being
                able to come to work and supply-chain bottlenecks. This version
                provides predicted benefit-cost ratios for various resilience
                actions, or tactics, that your business can implement right now.
                These can play an important role in helping you identify the
                most cost-effective actions to take. The full release of the BCR
                will include additional metrics and modules.
              </p>
              <h2>
                What is
                <span>resilience</span>, and what does it mean for your
                business?
              </h2>
              <p>
                Each disruption your business faces causes some degree of
                business interruption (BI). These BI losses lead to declines in
                sales revenue and profits. Note that BI losses begin when the
                disaster strikes, and continue until your business has
                recovered.
              </p>
              <p>
                For some recent major catastrophes, BI losses have rivaled or
                even exceeded property damage in dollar terms. For example, BI
                losses were four times the value of the World Trade Center in
                just the first year after 9/11. In the case of Hurricane
                Katrina, BI losses eventually exceeded property damage due to
                the protracted recovery.
              </p>
              <p>
                Resilience is all about reducing BI through tactics you can
                implement to help your business rebound. The BRC helps you
                assess your current resilience capability, compare it with other
                businesses like yours, and identify ways to improve it.
              </p>
            </section>
            <img src="/images/Picture20.png" alt class="content__image" />
            <section class="part part__four">
              <h2>Economic Disruptions during Covid-19</h2>
              <p>
                The COVID-19 pandemic differs from most other disasters in that
                it does not require repair and reconstruction of facilities.
                Instead, the main sources of BI stem from stay-at-home orders
                and social distancing measures, limited access to critical
                materials due to supply-chain bottlenecks, and communications
                disruptions due to overloaded communications networks. Even
                after mandatory restrictions are lifted, problems will persist
                as employees might be hesitant to return to work out of fear of
                contamination and critical materials may not be readily
                forthcoming because of supply-chain constraints.
              </p>
            </section>
            <img src="/images/Picture31.png" alt class="content__image" />
            <section class="part part__four">
              <h2>What to Do Next to Improve Your Resilience Capacity</h2>
              <p>
                The BRC provides a list of options that can help you make more
                informed decisions about the cost-effectiveness of resilience
                tactics. The guidance is differentiated by major firm
                characteristics, such as sector, size, magnitude of property
                damage, type of critical input disrupted, etc.
              </p>
              <p>
                After you use the BRC, there are many steps that you can take
                once you are equipped with this information. These include:
              </p>
              <ul>
                <li>
                  Intra-firm improvements, such as constructing more storage
                  space for inventories, considering options to recycle water,
                  purchasing back-up electricity generators, identifying
                  alternative locations in advance
                </li>
                <li>
                  Inter-firm improvements, such as cooperating with other firms
                  to cope with the disaster (e.g., pooling resources in short
                  supply, exchanging resources, mutually advantageous priorities
                  and timing of clearing of debris)
                </li>
                <li>
                  Regulatory improvements, such as possibilities of suspending
                  regulations that may be an obstacle to recovery (e.g.,
                  inter-firm cooperation that might otherwise violate anti-trust
                  laws, suspension of environmental regulations under special
                  circumstances)
                </li>
                <li>
                  Incentive identification, such as options for securing
                  low-interest loans government subsidies, philanthropy
                </li>
              </ul>
              <h2>Resources to Further Improve Your Resilience Capacity</h2>
              <p>
                Now that you’ve done a self-assessment of your firms' resilience
                capability and compared it to other businesses like yours, it
                might be helpful to identify ways to further improve your
                resilience. We provide two sets of sources that you can use for
                this important goal:
              </p>

              <div class="ordered-list">
                <ol>
                  <li>Sources of funding</li>
                  <li>Sources of information</li>
                </ol>
              </div>

              <p>
                You can then use these resources in the unique context of your
                business to improve its continuity in the event of future
                disasters. This table provides links to funding information for
                COVID-19. Eight federal programs are listed, both specific to
                the coronavirus and broader in scope, which your business may be
                able to use for coronavirus relief. The Table also identifies
                how these programs are applicable in a major way (
                <span class="table-font">
                  <font-awesome-icon
                    :icon="['fas', 'square']"
                    class="orange-square"
                  />
                </span>
                ) or minor way (
                <span class="table-font">
                  <font-awesome-icon
                    :icon="['fas', 'circle']"
                    size="xs"
                    class="bright-blue-circle"
                  />
                </span>
                ) to various resilience tactics you may be able to implement.
              </p>
            </section>
            <div class="domain-info--head">Coronavirus Relief Programs</div>
            <div class="table-wrapper">
              <table id="covid-table">
                <thead>
                  <tr>
                    <th>Program</th>
                    <th>Amount of Funding Available</th>
                    <th>Comments</th>
                    <th>Relevant Tactics</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.rd.usda.gov/programs-services/business-and-industry-cares-act-program"
                        target="_blank"
                        >Business and Industry CARES Act Program</a
                      >
                      (US Department of Agriculture)
                    </td>
                    <td class="content-col">
                      Loans that cannot exceed $25 million
                    </td>
                    <td class="content-col comments">
                      Market interest rates plus fees. Max term is 10 years.
                      Must be used by rural businesses and agricultural
                      producers as working capital to prevent, prepare for or
                      respond to the pandemic. Cannot be used for purchase of
                      equipment.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Input Substitution
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Relocation
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.sba.gov/funding-programs/disaster-assistance/coronavirus-covid-19"
                        target="_blank"
                        >Economic Injury Disaster Loans</a
                      >
                      (Small Business Administration)
                    </td>
                    <td class="content-col">
                      Loans originally capped at $2 million. As of April 2020,
                      included $10,000 advance grant. As of May 2020, limited to
                      $150,000
                    </td>
                    <td class="content-col comments">
                      Interest rate of 3.75%, or 2.75% for non-profits. Intended
                      to fund expenses such as payroll. As of May 2020, only
                      available to agricultural businesses.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Input Substitution
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.irs.gov/coronavirus/employee-retention-credit"
                        target="_blank"
                        >Employee Retention Credit</a
                      >
                      (Internal Revenue Service)
                    </td>
                    <td class="content-col">
                      Tax credits equal to 50% of up to $10,000 in qualified
                      wages, capped at $5,000 per employee
                    </td>
                    <td class="content-col comments">
                      Employers receiving a loan from the Paycheck Protection
                      Program are not eligible for the tax credit.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Input Substitution
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.federalreserve.gov/monetarypolicy/mainstreetlending.htm"
                        target="_blank"
                        >Main Street Lending Program</a
                      >
                      (Federal Reserve)
                    </td>
                    <td class="content-col">
                      Loans of minimum size of $500,000, and maximum size of
                      $500 million
                    </td>
                    <td class="content-col comments">
                      Two to four years at LIBOR plus 3%. Designed to support
                      medium-sized and small businesses and to help companies in
                      sound financial condition prior to the pandemic maintain
                      their operations.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Input Substitution
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Relocation
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Technological Change
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.sba.gov/funding-programs/loans/coronavirus-relief-options/paycheck-protection-program"
                        target="_blank"
                        >Paycheck Protection Program</a
                      >
                      (Small Business Administration)
                    </td>
                    <td class="content-col">
                      Loan of up to 2.5x average monthly payroll for 2019 or
                      comparable seasonal period up to $10 million
                    </td>
                    <td class="content-col comments">
                      Fixed interest rate of 1%. Fully or partially forgivable
                      if at least 75% is used for payroll, and employer does not
                      reduce payrates and number of full-time equivalent
                      employees.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Input Substitution
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.federalreserve.gov/monetarypolicy/pmccf.htm"
                        target="_blank"
                        >Primary Market Corporate Credit Facility</a
                      >
                      (Federal Reserve)
                    </td>
                    <td class="content-col">
                      Loans up to $11.25 billion per company
                    </td>
                    <td class="content-col comments">
                      A funding backstop for corporate debt issued by eligible
                      issuers. Market interest rate plus 1% fee. Open to
                      investment grade companies.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Input Substitution
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Relocation
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Technology Change
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.sba.gov/funding-programs/loans/coronavirus-relief-options/sba-debt-relief"
                        target="_blank"
                        >SBA Debt Relief</a
                      >
                      (Small Business Administration)
                    </td>
                    <td class="content-col">
                      Loan forgiveness for six months of principal, interest,
                      and fees that borrowers owe on current and future 7(a),
                      504, and Microloans from the SBA
                    </td>
                    <td class="content-col comments">
                      Equivalent to a grant. Relief not available for Paycheck
                      Protection Program or Economic Injury Disaster loans.
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Input Substitution
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Relocation
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Technology Change
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="content-col">
                      <a
                        href="https://www.sba.gov/partners/lenders/7a-loan-program/types-7a-loans"
                        target="_blank"
                        >SBA Traditional 7(a)</a
                      >
                      (Small Business Administration)
                    </td>
                    <td class="content-col">Loans of up to $5 million</td>
                    <td class="content-col comments">
                      Market interest rates capped at SBA max. Loans must
                      typically be repaid in 7-10 years. Funds for financing and
                      equipment
                    </td>
                    <td class="content-col">
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          class="orange-square"
                        />Production Recapture
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Resource Isolation
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Relocation
                      </div>
                      <div class="row">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="bright-blue-circle"
                        />Technology Change
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <section class="part part__four">
              <p>
                All eight programs appear to provide significant support for
                Production Recapture, as they are intended to provide funds for
                businesses affected by the pandemic maintain or resume
                activities. They also provide funding support for workers, so
                businesses can avoid having to find substitutes for their
                pre-disaster labor force. However, the sources will have varying
                effects on the other resilience tactics depending on program
                design, eligibility rules and the current context depending on
                how you choose to use those funds. For example, funding sources
                are not relevant to the resilience tactics that need to be put
                in place before the disaster, the pandemic in this case (e.g.,
                inventories, excess capacity).
              </p>

              <p>
                The listed funding programs fall into two categories: 1) loans
                and credits for operating expenses, and 2) loans with fewer
                restrictions. Several programs fund operating expenses such as
                payroll, utilities, and leases, but not business expansion.
                Those programs, which include the Business and Industry CARES
                Act Program for rural businesses and the Economic Injury
                Disaster Loans, offer minor support for input substitution as
                they subsidize labor and utilities, such as energy and water,
                but do not support other production inputs.
              </p>

              <p>
                Two other initiatives, the Employee Retention Credit and the
                Paycheck Protection Program (PPP), are designed to offer direct
                subsidies for businesses to rehire workers or to keep them
                employed. Thus, those programs appear to provide major support
                for input substitution to help you overcome labor shortages. The
                other set of programs offers less restrictive loans and credits
                and that can be used for expansion or renovation, including the
                purchase of equipment or real estate. These programs include two
                special purpose instruments established by the Federal Reserve:
                1) the Primary Market Corporate Credit Facility for large
                corporations, and 2) the Main Street Lending Program for small
                and medium size businesses. The table also includes Small
                Business Administration 7(a) loans and debt relief. As these
                programs have few or no restrictions, they are designed to offer
                minor support for businesses on tactics such as relocation,
                technological change, and input substitution. You may be able to
                use the Fed’s Main Street Lending Program, which offers major
                support for input substitution on the labor side, since it
                requires that loan recipients make “commercially reasonable
                efforts” to maintain payroll.
              </p>
            </section>
            <div class="link-container">
              <nuxt-link class="close-link" :to="continueWhere"
                >Close</nuxt-link
              >
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSquare, faCircle } from "@fortawesome/free-solid-svg-icons";
import consola from "consola";

library.add({ faSquare, faCircle });

export default {
  computed: {
    continueWhere() {
      consola.info(
        `continueWhere results=${this.$store.state.results?.length}`
      );
      if (this.$store.state.results?.length) {
        return "/results";
      } else if (
        Object.values(this.$store.getters.profileData).every(
          o => o === "" || o === undefined
        )
      ) {
        return "/info";
      } else {
        return "/scenario";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.bg-override {
  background: white;
}
.covid-19 {
  width: 100%;
  max-width: 714px;
  margin: 0 auto;
}
.covid-19 a.close-link {
  text-decoration: none;
  padding-left: 5px;
  font-style: italic;
  font-weight: bold;
  color: var(--bright-blue);
}
.link-container {
  padding-bottom: 40px;
}
.bright-blue-circle {
  color: var(--bright-blue);
}
.orange-square {
  color: var(--banner);
}
.table-wrapper {
  margin-bottom: 1em;
}
.table-font {
  font-size: 11pt;
  line-height: 14pt;
}
#covid-table {
  /* font-family: "Gotham A", "Gotham B", sans-serif; */
  font-size: 11pt;
  line-height: 14pt;
  margin: 1rem auto 0.5rem auto;
  border-collapse: collapse;
  th,
  td {
    border: 1px solid black;
    padding: 4px;
    min-width: 18px;
  }
  td.comments {
    min-width: 125px !important;
  }
  th {
    text-align: left;
  }
  .content-col {
    max-width: 250px;
    vertical-align: top;
  }
  .mark-col {
    text-align: center;
  }
  td:nth-of-type(14) {
    vertical-align: top;
    max-width: 300px;
  }
}
.covid-table-key {
  /* font-family: "Gotham A", "Gotham B", sans-serif; */
  font-size: 11pt;
  line-height: 14pt;
  width: 100%;
  div:first-of-type {
    padding: 0 !important;
  }
}
.key-entry {
  display: flex;
  div:first-child {
    font-weight: bold;
    min-width: 2.5em;
  }
}
.mx-auto {
  margin-right: auto;
  margin-left: auto;
}
.content__header {
  font-family: "Tungsten A", "Tungsten B", sans-serif;
  text-align: center;
  background-color: var(--banner);
  color: white;
  border-radius: 50%;
  font-size: 70px;
  line-height: 140px;
  width: 140px;
  height: 140px;
}
.link-container {
  max-width: 631px;
  margin-left: auto;
  margin-right: auto;
}
.part {
  text-align: center;
  max-width: 631px;
  margin-left: auto;
  margin-right: auto;
  p {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
}
.content__image {
  width: 108%;
  margin-left: -25px;
}
.part__three,
.part__four {
  text-align: left !important;
}
.part__three h2,
.part__four h2 {
  color: var(--dark-blue);
  padding-left: 10px;
  padding-right: 10px;
}
.part__three h2 span {
  color: var(--bright-blue);
}
.part__one p {
  font-size: 14pt;
  line-height: 18pt;
  padding: 0 20px 20px 20px;
}
.part__one p span {
  font-size: 125%;
  color: var(--banner);
  font-weight: 500;
}
.part__two h2 {
  color: var(--bright-blue);
  font-weight: 500;
  font-style: italic;
}
.part__two .blue__bar {
  background-color: var(--bright-blue);
  height: 9px;
  width: 60px;
  margin-left: auto;
  margin-right: auto;
}
.part__two div:first-child {
  padding-top: 0 !important;
  margin-bottom: 20px;
}
.content__image {
  margin-top: 20px;
}
ul {
  padding-left: 10px;
  margin-left: 10px;
}
ul li {
  list-style-position: outside;
  list-style-type: disc;
  margin-top: 0;
}
.ordered-list {
  padding-top: 0 !important;
}
.ordered-list ol {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0;
  margin-bottom: 0;
}
.link-container {
  padding-left: 10px;
}
.row {
  display: flex;
}
.row svg {
  margin-right: 5px;
  padding-top: 3px;
}
</style>
